import { HttpClient } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { Router } from '@angular/router';

import { AppError } from '@hglobal/common';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { map, switchMap, throwError } from 'rxjs';

import { AccessRightsResponse, parseRoleFromApi, Role, Team } from '@pp/core/entities';
import { environment } from '@pp/environment';

const ORG_ID_FOR_ALL_ORGS = -1;
const ORG_ID_KEY = 'org-id';
const ORG_IDS_KEY = 'org-ids';

interface StorageState {
  readonlyRights: boolean;
  organizations: Team[] | null;
  accessRights: AccessRightsResponse | null;
  orgId: number | unknown | null;
  orgIds: number[] | null;
}

const initialState: StorageState = {
  readonlyRights: false,
  organizations: null,
  accessRights: null,
  orgId: null,
  orgIds: null,
};

export const StorageStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(store => ({
    role: computed(() => (store.accessRights()?.role ? parseRoleFromApi(store.accessRights().role) : null)),
    permissions: computed(() => {
      const rights = store.accessRights();
      return rights?.permissions ? new Map(rights.permissions.map(item => [item.module, item.actions])) : null;
    }),
    orgIdsWithNames: computed(() => store.organizations()?.map(org => ({ name: org.name, id: org.orgId })) || []),
    manuallySelectedOrgId: computed(() => {
      const role = store.accessRights()?.role ? parseRoleFromApi(store.accessRights().role) : null;
      return role === Role.SuperAdmin ? ORG_ID_FOR_ALL_ORGS : store.orgId();
    }),
  })),
  withMethods(store => ({
    readonlyRightsSet(readonlyRights: boolean) {
      patchState(store, { readonlyRights });
    },

    getStoredOrgId(): number {
      return +localStorage.getItem(ORG_ID_KEY);
    },

    storeOrgId(orgId: number): void {
      localStorage.setItem(ORG_ID_KEY, orgId.toString());
    },

    getStoredOrgIds(): number[] {
      const stored = localStorage.getItem(ORG_IDS_KEY);
      return stored ? stored.split(',').map(Number) : [];
    },

    storeOrgIds(orgIds: number[]): void {
      localStorage.setItem(ORG_IDS_KEY, orgIds.join(','));
    },

    removeStoredOrgId(): void {
      localStorage.removeItem(ORG_ID_KEY);
      localStorage.removeItem(ORG_IDS_KEY);
    },
  })),
  withMethods(store => {
    const http = inject(HttpClient);
    const router = inject(Router);
    const NO_ORGANIZATIONS_ERROR_CODE = '136';

    return {
      loadOrganizations: rxMethod<void>(
        switchMap(() => {
          const params = { statuses: 'active' };
          return http.get<{ organizations: Team[] }>(`${environment.apiHost}organizations/`, { params }).pipe(
            map(res => res.organizations),
            tapResponse({
              next: organizations => {
                const orgIds = organizations.map(org => org.orgId);
                const orgId = store.getStoredOrgId() || orgIds[0];
                store.storeOrgId(orgId);
                store.storeOrgIds(orgIds);
                patchState(store, { organizations, orgIds, orgId });
              },
              error: (error: AppError) => {
                if (error?.originalError?.error?.error_code === NO_ORGANIZATIONS_ERROR_CODE) {
                  router.navigate(['/no-team']);
                }
                return throwError(() => error);
              },
            }),
          );
        }),
      ),

      loadAccessRights: rxMethod<void>(
        switchMap(() => {
          return http.get<AccessRightsResponse>(`${environment.apiHost}permissions/access`).pipe(
            tapResponse({
              next: accessRights => patchState(store, { accessRights }),
              error: () => {},
            }),
          );
        }),
      ),

      onLogout(): void {
        patchState(store, {
          readonlyRights: false,
          organizations: null,
          accessRights: null,
          orgId: null,
          orgIds: null,
        });
        store.removeStoredOrgId();
      },
    };
  }),
);
